import { useGo, useCustom, useCustomMutation, useList } from "@refinedev/core";
import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Loader2, Plus, Trash2, Bell, Zap, Globe, ShieldCheck, ShieldOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ColorPicker } from "@/components/ui/color-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FormHeader, FormStickyActions, FormSpacer } from "@/components/forms";
import type { OrderStatusConfig, OrderStatusAction } from "./types";

const ACTION_TYPES = [
  { value: 'grant_access', label: 'Выдать доступ', icon: ShieldCheck, description: 'Автоматически выдать доступ к купленным товарам' },
  { value: 'revoke_access', label: 'Отозвать доступ', icon: ShieldOff, description: 'Отозвать выданный ранее доступ' },
  { value: 'send_notification', label: 'Отправить уведомление', icon: Bell, description: 'Отправить уведомление по шаблону' },
  { value: 'trigger', label: 'Выполнить триггер', icon: Zap, description: 'Запустить настроенный триггер' },
  { value: 'webhook', label: 'Вызвать вебхук', icon: Globe, description: 'Отправить HTTP запрос на внешний URL' },
] as const;

interface Trigger {
  id: number;
  name: string;
  event: string;
  isActive: boolean;
}

interface ActionEditorProps {
  action: OrderStatusAction;
  onChange: (action: OrderStatusAction) => void;
  onRemove: () => void;
  triggers: Trigger[];
  isLoadingTriggers: boolean;
}

function ActionEditor({
  action,
  onChange,
  onRemove,
  triggers,
  isLoadingTriggers,
}: ActionEditorProps) {
  const actionType = ACTION_TYPES.find(t => t.value === action.type);
  const Icon = actionType?.icon ?? Zap;

  return (
    <div className="flex items-start gap-3 p-4 bg-muted/30 rounded-lg border">
      <div className="p-2 rounded-lg bg-background">
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>

      <div className="flex-1 space-y-3">
        <Select
          value={action.type}
          onValueChange={(value) =>
            onChange({ type: value as OrderStatusAction['type'] })
          }
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ACTION_TYPES.map((t) => {
              const TypeIcon = t.icon;
              return (
                <SelectItem key={t.value} value={t.value}>
                  <div className="flex items-center gap-2">
                    <TypeIcon className="h-4 w-4" />
                    <span>{t.label}</span>
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        {actionType && (
          <p className="text-xs text-muted-foreground">
            {actionType.description}
          </p>
        )}

        {action.type === 'send_notification' && (
          <div className="space-y-3">
            <div className="space-y-2">
              <Label className="text-xs">Заголовок уведомления</Label>
              <Input
                placeholder="Заголовок"
                value={action.title ?? ''}
                onChange={(e) => onChange({ ...action, title: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Текст уведомления</Label>
              <Input
                placeholder="Текст сообщения"
                value={action.message ?? ''}
                onChange={(e) => onChange({ ...action, message: e.target.value })}
              />
            </div>
          </div>
        )}

        {action.type === 'trigger' && (
          <div className="space-y-2">
            <Label className="text-xs">Триггер</Label>
            {isLoadingTriggers ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Загрузка триггеров...
              </div>
            ) : (triggers.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Нет доступных триггеров. Создайте триггер в разделе "Триггеры".
              </p>
            ) : (
              <Select
                value={action.triggerId?.toString() ?? ''}
                onValueChange={(value) =>
                  onChange({ ...action, triggerId: parseInt(value, 10) })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Выберите триггер" />
                </SelectTrigger>
                <SelectContent>
                  {triggers.map((trigger) => (
                    <SelectItem key={trigger.id} value={trigger.id.toString()}>
                      <div className="flex items-center gap-2">
                        <span>{trigger.name}</span>
                        {!trigger.isActive && (
                          <span className="text-xs text-orange-500">(неактивен)</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ))}
          </div>
        )}

        {action.type === 'webhook' && (
          <div className="space-y-3">
            <div className="space-y-2">
              <Label className="text-xs">URL вебхука</Label>
              <Input
                placeholder="https://example.com/webhook"
                value={action.url ?? ''}
                onChange={(e) => onChange({ ...action, url: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">HTTP метод</Label>
              <Select
                value={action.method ?? 'POST'}
                onValueChange={(value) =>
                  onChange({ ...action, method: value as 'GET' | 'POST' })
                }
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="GET">GET</SelectItem>
                  <SelectItem value="POST">POST</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>

      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onRemove}
        className="text-muted-foreground hover:text-destructive"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

export function OrderStatusForm() {
  const go = useGo();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id?: string }>();
  const isEdit = !!id;

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState("#6b7280");
  const [isSystem, setIsSystem] = useState(false);
  const [isInitial, setIsInitial] = useState(false);
  const [isFinal, setIsFinal] = useState(false);
  const [onEnterActions, setOnEnterActions] = useState<OrderStatusAction[]>([]);
  const [onExitActions, setOnExitActions] = useState<OrderStatusAction[]>([]);

  // Load triggers
  const triggersQuery = useList<Trigger>({
    resource: "triggers",
    pagination: { pageSize: 100 },
  });
  const triggers = triggersQuery.result?.data ?? [];
  const isLoadingTriggers = triggersQuery.query?.isLoading ?? false;

  // Load status data for edit
  const statusQuery = useCustom<OrderStatusConfig>({
    url: `/order-statuses/${id}`,
    method: "get",
    queryOptions: { enabled: isEdit },
  });

  const statusData = statusQuery.query?.data?.data;
  const isLoadingStatus = statusQuery.query?.isLoading ?? false;

  // Get all statuses for position
  const allStatusesQuery = useCustom<OrderStatusConfig[]>({
    url: "/order-statuses",
    method: "get",
  });

  const allStatuses = allStatusesQuery.query?.data?.data ?? [];

  const createMutation = useCustomMutation();
  const createStatus = createMutation.mutate;
  const isCreating = createMutation.mutation.isPending;

  const updateMutation = useCustomMutation();
  const updateStatus = updateMutation.mutate;
  const isUpdating = updateMutation.mutation.isPending;

  const isSaving = isCreating || isUpdating;

  // Populate form for edit
  useEffect(() => {
    if (statusData) {
      setName(statusData.name);
      setDescription(statusData.description ?? "");
      setColor(statusData.color);
      setIsSystem(statusData.isSystem);
      setIsInitial(statusData.isInitial);
      setIsFinal(statusData.isFinal);
      setOnEnterActions(statusData.onEnterActions);
      setOnExitActions(statusData.onExitActions);
    }
  }, [statusData]);

  const handleSubmit = () => {
    const position = isEdit
      ? statusData?.position ?? 0
      : (allStatuses.length ?? 0);

    const values = {
      name,
      description: description || undefined,
      color,
      position,
      isInitial,
      isFinal,
      onEnterActions,
      onExitActions,
    };

    if (isEdit) {
      // For system statuses, only send allowed fields
      const isSystemStatus = statusData?.isSystem ?? false;
      const payload = isSystemStatus
        ? { name, description: description || undefined, color, onEnterActions, onExitActions }
        : values;

      updateStatus(
        {
          url: `/order-statuses/${id}`,
          method: "put",
          values: payload,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['data', 'default', 'order-statuses'] });
            go({ to: "/orders/statuses" });
          },
        }
      );
    } else {
      createStatus(
        {
          url: "/order-statuses",
          method: "post",
          values,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['data', 'default', 'order-statuses'] });
            go({ to: "/orders/statuses" });
          },
        }
      );
    }
  };

  const handleBack = () => go({ to: "/orders/statuses" });

  const addAction = (isEnter: boolean) => {
    const newAction: OrderStatusAction = { type: 'grant_access' };
    if (isEnter) {
      setOnEnterActions([...onEnterActions, newAction]);
    } else {
      setOnExitActions([...onExitActions, newAction]);
    }
  };

  const updateEnterAction = (index: number, action: OrderStatusAction) => {
    const updated = [...onEnterActions];
    updated[index] = action;
    setOnEnterActions(updated);
  };

  const updateExitAction = (index: number, action: OrderStatusAction) => {
    const updated = [...onExitActions];
    updated[index] = action;
    setOnExitActions(updated);
  };

  const removeEnterAction = (index: number) => {
    setOnEnterActions(onEnterActions.filter((_, i) => i !== index));
  };

  const removeExitAction = (index: number) => {
    setOnExitActions(onExitActions.filter((_, i) => i !== index));
  };

  if (isEdit && isLoadingStatus) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <FormHeader
        title={isEdit ? "Редактировать статус" : "Создать статус"}
        description={isEdit ? `Редактирование статуса ${name}` : "Создание нового статуса заказа"}
        onBack={handleBack}
        sticky
      />

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle>Основная информация</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Название</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="В работе"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Описание</Label>
              <Input
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Заказ находится в обработке менеджером"
              />
              <p className="text-xs text-muted-foreground">
                Краткое описание назначения статуса
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="color">Цвет</Label>
              <ColorPicker id="color" value={color} onChange={setColor} />
            </div>
          </CardContent>
        </Card>

        {/* Flags */}
        <Card>
          <CardHeader>
            <CardTitle>Параметры</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isSystem && (
              <div className="flex items-center justify-between p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                <div>
                  <Label className="text-amber-700 font-semibold">Системный статус</Label>
                  <p className="text-xs text-amber-600/80">
                    Системные статусы управляются приложением
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div>
                <Label>Начальный статус</Label>
                <p className="text-xs text-muted-foreground">
                  Присваивается новым заказам
                </p>
              </div>
              <Switch
                checked={isInitial}
                onCheckedChange={setIsInitial}
                disabled={isSystem}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Финальный статус</Label>
                <p className="text-xs text-muted-foreground">
                  Заказ больше не может измениться
                </p>
              </div>
              <Switch
                checked={isFinal}
                onCheckedChange={setIsFinal}
                disabled={isSystem}
              />
            </div>

            {isSystem && (
              <div className="p-3 bg-muted rounded-lg text-sm text-muted-foreground">
                Системный статус: некоторые параметры нельзя изменить
              </div>
            )}
          </CardContent>
        </Card>

        {/* Enter Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Действия при входе</CardTitle>
            <CardDescription>
              Выполняются когда заказ переходит в этот статус
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {onEnterActions.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground">
                <p className="text-sm">Нет действий</p>
                <p className="text-xs mt-1">Добавьте действие, которое выполнится при переходе заказа в этот статус</p>
              </div>
            ) : (
              onEnterActions.map((action, index) => (
                <ActionEditor
                  key={index}
                  action={action}
                  onChange={(a) => updateEnterAction(index, a)}
                  onRemove={() => removeEnterAction(index)}
                  triggers={triggers}
                  isLoadingTriggers={isLoadingTriggers}
                />
              ))
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => addAction(true)}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Добавить действие
            </Button>
          </CardContent>
        </Card>

        {/* Exit Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Действия при выходе</CardTitle>
            <CardDescription>
              Выполняются когда заказ покидает этот статус
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {onExitActions.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground">
                <p className="text-sm">Нет действий</p>
                <p className="text-xs mt-1">Добавьте действие, которое выполнится при выходе заказа из этого статуса</p>
              </div>
            ) : (
              onExitActions.map((action, index) => (
                <ActionEditor
                  key={index}
                  action={action}
                  onChange={(a) => updateExitAction(index, a)}
                  onRemove={() => removeExitAction(index)}
                  triggers={triggers}
                  isLoadingTriggers={isLoadingTriggers}
                />
              ))
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => addAction(false)}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Добавить действие
            </Button>
          </CardContent>
        </Card>
      </div>

      <FormStickyActions
        primaryLabel={isEdit ? "Сохранить" : "Создать"}
        secondaryLabel="Отмена"
        onPrimary={handleSubmit}
        onSecondary={handleBack}
        isPending={isSaving}
        pendingLabel={isEdit ? "Сохранение..." : "Создание..."}
      />

      <FormSpacer />
    </div>
  );
}
